import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ClickOutside from '../ClickOutside';
import Container from '../Container';
import Popup from '../Popup';
import Menu from '../Menu';
import Avatar from '../Avatar';
import Text from '../Text';
import IconUser from './IconUser';
import IconAvatar from './IconAvatar';
import IconManageAccount from './IconManageAccount';
import IconLogOut from './IconLogOut';

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all ease 0.3s;
  background: none;
  border: none;
  width: 100%;
  height: 100%;
  &:focus {
    outline: none;
  }
`;

const UserMenu = ({
  auth,
  user,
  imageResizer,
  iconFill,
  iconFillActive,
  isUserManagement,
  menuItems,
  onOpen,
  onClose,
  ...props
}) => {
  const { displayName, avatar } = user;
  const { login, logout, token } = auth;

  const [show, setShow] = useState(false);
  const [buttonHover, setButtonHover] = useState(false);

  const loggedInItems = [
    ...(menuItems && menuItems.length ? [...menuItems] : []),
    {
      splitter: true,
      key: 'splitter1',
    },
    ...(!isUserManagement
      ? [
          {
            text: 'Manage Account',
            icon: (
              <Container
                width="34px"
                marginRight="10px"
                display="flex"
                justifyContent="center"
              >
                <IconManageAccount />
              </Container>
            ),
            onClick: () => {
              window.open('https://account.localsearch.com.au', '_blank');
            },
            key: 'manageAccount',
          },
          {
            splitter: true,
            key: 'splitter2',
          },
        ]
      : []),
    {
      text: 'Log Out',
      icon: (
        <Container
          width="34px"
          marginRight="10px"
          display="flex"
          justifyContent="center"
        >
          <IconLogOut />
        </Container>
      ),
      onClick: logout,
      key: 'logout',
    },
  ];

  const loggedOutItems = [
    {
      text: 'Log In',
      onClick: () => login({ redirectUrl: window.location.pathname }),
      key: 'logIn',
    },
    {
      splitter: true,
      key: 'splitter',
    },
    {
      text: 'Create New Account',
      onClick: () =>
        login({ redirectUrl: window.location.pathname, mode: 'register' }),
      key: 'createNewAccount',
    },
  ];

  const getAvatarResizeSrc = () => {
    if (!imageResizer) return avatar;
    return imageResizer.resize(avatar, { width: 140, height: 140 });
  };

  return (
    <ClickOutside
      onClickOutside={() => {
        setShow(false);
        if (show) {
          onClose();
        }
      }}
      borderRadius="100%"
      backgroundColor="white"
      {...props}
    >
      <StyledButton
        onMouseEnter={() => setButtonHover(true)}
        onMouseLeave={() => setButtonHover(false)}
        onClick={() => {
          setShow(!show);
          onOpen();
        }}
        type="button"
      >
        {avatar ? (
          <Avatar width="100%" objectFit="cover" src={getAvatarResizeSrc()} />
        ) : (
          <IconUser
            width="65%"
            fill={buttonHover || show ? iconFillActive : iconFill}
          />
        )}
      </StyledButton>
      <Popup
        anchor="topRight"
        width={token() ? '240px' : '210px'}
        marginTop="12px"
        show={show}
      >
        {token() && (
          <Container
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            padding="20px 10px 5px 10px"
          >
            {avatar ? (
              <Avatar width="70px" height="70px" src={getAvatarResizeSrc()} />
            ) : (
              <IconAvatar />
            )}
            {displayName && (
              <Text
                fontSize="18px"
                color="normal"
                lineHeight="1.2"
                fontFamily="circular"
                fontWeight="bold"
                marginTop="15px"
                textAlign="center"
              >
                {displayName}
              </Text>
            )}
          </Container>
        )}
        <Menu data={token() ? loggedInItems : loggedOutItems} />
      </Popup>
    </ClickOutside>
  );
};

UserMenu.propTypes = {
  iconFill: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  iconFillActive: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  auth: PropTypes.shape({
    login: PropTypes.func,
    logout: PropTypes.func,
    token: PropTypes.func,
  }),
  user: PropTypes.shape({
    displayName: PropTypes.string,
    avatar: PropTypes.string,
  }),
  config: PropTypes.shape({}),
  isUserManagement: PropTypes.bool,
  menuItems: PropTypes.arrayOf(PropTypes.shape({})),
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  imageResizer: PropTypes.shape({
    resize: PropTypes.func,
  }),
};

UserMenu.defaultProps = {
  iconFill: 'black',
  iconFillActive: '#97AEFF',
  auth: {
    login: () => {},
    logout: () => {},
    token: () => {},
  },
  user: {},
  config: null,
  isUserManagement: false,
  menuItems: [],
  onOpen: () => {},
  onClose: () => {},
  imageResizer: null,
};

export default UserMenu;
